<template>
  <div clas="flex flex-col gap-4">
    <!-- Assets selection and filter -->
    <div class="flex h-[40px] flex-none items-center justify-between gap-4">
      <div class="flex w-full items-center gap-1 text-sm">
        <span
          v-if="selectedAssetsCount"
          class="flex size-6 items-center justify-center rounded-full bg-main-primary text-white"
        >
          <CheckRoundedSmall
            bg-color="bg-main-primary"
            custom-style="size-3.5"
          />
        </span>
        <span
          v-else
          class="flex size-6 items-center justify-center rounded-full bg-main-primary px-1.5 py-0.5 text-xs text-white"
        >2</span>
        <span class="whitespace-nowrap font-medium text-gray-700">Step 2 - Select by clicking on the asset(s)</span>
      </div>
      <div
        class="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700"
      >
        <span class="text-xs text-main-primary/70">{{ selectedAssetsCount }} Asset(s)
          <span class="text-gray-700">Selected</span></span>
        <div class="flex items-center gap-1">
          <div class="relative z-50">
            <button
              class="text-gray-400 outline-none"
              :class="{ 'bg-gray-100 text-gray-700': showFilter }"
              @click="showFilter = !showFilter"
            >
              <FilterIcon />
            </button>
            <div
              v-if="showFilter"
              class="absolute left-0 top-[105%] z-50"
            >
              <div
                class="relative max-h-[250px] w-full overflow-y-auto rounded-lg bg-white text-left text-main-primary shadow-md"
              >
                <div class="bg-main-primary p-3 text-white">
                  Assets
                </div>
                <div class="flex flex-col">
                  <button
                    type="button"
                    class="w-full px-3 py-2 text-left outline-none focus:bg-gray-100"
                    @click="filterAssets(true)"
                  >
                    Assigned Assets
                  </button>
                  <button
                    type="button"
                    class="w-full px-3 pb-2 text-left outline-none focus:bg-gray-100"
                    @click="filterAssets(false)"
                  >
                    Unassigned Assets
                  </button>
                </div>
              </div>
            </div>
          </div>
          <span>Filter</span>
        </div>
        <InputWithIcon
          classes="!py-1.5 !text-sm !min-w-[130px] placeholder:text-sm"
          placeholder="Search..."
          @input="assetQuery = $event"
        >
          <template #icon>
            <SearchIcon />
          </template>
        </InputWithIcon>
      </div>
    </div>
    <!-- Assets graph -->
    <div
      :id="`assign-assets-tree-${uid}`"
      class="grow pt-4"
    >
      <EsgAssignAssetTreeNode
        v-if="canDisplayAssetsTree"
        :node="assetsTree"
        @toggle-selection="handleToggleProp($event, 'selected')"
        @toggle-children="handleToggleProp($event, 'expandChildren')"
      />
    </div>
  </div>
</template>

<script>
import { assetsInjectionKey } from '@/utils/keys'

export default {
  name: 'EsgAssignableAssetsTree',

  inject: { assetsInjectionKey },

  props: {
    savedMetrics: {
      type: Array,
      required: true,
    },
    savedAssets: {
      type: Array,
      required: true,
    },
  },

  emits: ['assetsSelected'],

  data() {
    return {
      uid: null,
      assets: this.transformAssetsData(this.assetsInjectionKey),
      assetsTree: {},
      assetQuery: '',
      showFilter: false,
    }
  },

  computed: {
    canDisplayAssetsTree() {
      return this.assetsTree?.id
    },
    selectedAssetsCount() {
      const assets = JSON.parse(JSON.stringify(this.assets))
      return assets.filter(({ selected }) => selected).length
    },
  },

  watch: {
    showFilter(isShown) {
      if (!isShown) {
        this.showAllAssets()
      }
    },
    assetsInjectionKey(newAssets) {
      this.assets = this.transformAssetsData(newAssets)
    },
    assets: {
      deep: true,
      immediate: true,
      handler(newAssets) {
        this.assetsTree = this.getTreeNodesFromAssets(newAssets)
        const selectedAssets = JSON.parse(JSON.stringify(newAssets))
          .filter(({ selected }) => selected)
          .map((asset) => {
            delete asset.children
            delete asset.expandChildren
            delete asset.selected
            return asset
          })
        this.$emit('assetsSelected', selectedAssets)
      },
    },
    assetQuery(newQuery) {
      if (newQuery) {
        this.assets = this.assets.map((asset) => {
          if (
            !asset.asset_name.toLowerCase().includes(newQuery.toLowerCase())
          ) {
            asset.hide = true
          }
          return asset
        })
      }
      else {
        this.showAllAssets()
      }
    },
  },

  mounted() {
    this.uid = this.$.uid
  },

  methods: {
    transformAssetsData(assets) {
      const assetsData = JSON.parse(JSON.stringify(assets))

      return assetsData.map((asset) => {
        if (asset.selected === undefined) {
          asset.selected = false
        }
        if (asset.hide === undefined) {
          asset.hide = false
        }

        asset.metrics = [...asset.metrics].filter(({ metric_id }) =>
          this.savedMetrics.includes(metric_id),
        )
        asset.expandChildren = true
        return asset
      })
    },
    getTreeNodesFromAssets(assets) {
      const map = new Map()
      const tree = []

      assets.forEach((asset) => {
        map.set(asset.id, { ...asset, children: [] })
      })

      assets.forEach(({ id, parent_node }) => {
        if (parent_node !== null) {
          const parent = map.get(parent_node)
          if (parent) {
            parent.children.push(map.get(id))
          }
        }
        else {
          tree.push(map.get(id))
        }
      })

      return tree[0]
    },
    handleToggleProp(assetId, key) {
      this.assets = this.assets.map((asset) => {
        if (asset.id === assetId) {
          asset[key] = !asset[key]
        }
        return asset
      })
    },
    deselectAssets() {
      this.assets = this.assets.map((asset) => {
        asset.selected = false
        return asset
      })
    },
    filterAssets(isAssigned) {
      this.assets = this.assets.map((asset) => {
        asset.hide = isAssigned ? !asset.metrics.length : asset.metrics.length
        return asset
      })
    },
    showAllAssets() {
      this.assets = this.assets.map((asset) => {
        asset.hide = false
        return asset
      })
    },
  },
}
</script>
